// API client for the NarcCheck AI backend (web).
// Shares the exact backend contract used by the iOS app so history + auth sync.
// - Bearer session_token when signed in.
// - X-Device-Id for anonymous history scoping / migration on login.
const BASE = process.env.REACT_APP_BACKEND_URL;

const TOKEN_KEY = "narc.session_token";
const DEVICE_ID_KEY = "narc.device_id";

export function getToken() {
  try {
    return window.localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token) {
  try {
    if (token) window.localStorage.setItem(TOKEN_KEY, token);
    else window.localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

export function getDeviceId() {
  try {
    let id = window.localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id =
        (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
        `dev_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      window.localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

function authHeaders(extra = {}) {
  const headers = { ...extra };
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const deviceId = getDeviceId();
  if (deviceId) headers["X-Device-Id"] = deviceId;
  return headers;
}

async function jsonFetch(path, init = {}) {
  const headers = authHeaders({ "Content-Type": "application/json", ...(init.headers || {}) });
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      msg = body?.detail ?? msg;
    } catch {}
    throw new Error(typeof msg === "string" ? msg : JSON.stringify(msg));
  }
  return res.json();
}

export function analyze(req) {
  return jsonFetch("/api/analyze", { method: "POST", body: JSON.stringify(req) });
}

export async function transcribe(blob, filename = "recording.webm") {
  const form = new FormData();
  form.append("file", blob, filename);
  const res = await fetch(`${BASE}/api/transcribe`, {
    method: "POST",
    headers: authHeaders(), // no content-type, browser sets multipart boundary
    body: form,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      msg = (await res.json())?.detail ?? msg;
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

export function saveHistory(payload) {
  return jsonFetch("/api/history", { method: "POST", body: JSON.stringify(payload) });
}

export function listHistory() {
  return jsonFetch("/api/history");
}

export function getHistory(id) {
  return jsonFetch(`/api/history/${id}`);
}

export function deleteHistory(id) {
  return jsonFetch(`/api/history/${id}`, { method: "DELETE" });
}

// ---- auth ----
export function authMe() {
  return jsonFetch("/api/auth/me");
}

export function exchangeSession(sessionId) {
  return jsonFetch("/api/auth/session", {
    method: "POST",
    body: JSON.stringify({ session_id: sessionId, device_id: getDeviceId(), origin_url: window.location.origin }),
  });
}

export function loginEmail(email, password) {
  return jsonFetch("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password, device_id: getDeviceId() }),
  });
}

export function registerEmail(email, password, name) {
  return jsonFetch("/api/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password, name, device_id: getDeviceId(), origin_url: window.location.origin }),
  });
}

export function forgotPassword(email) {
  return jsonFetch("/api/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email, origin_url: window.location.origin }),
  });
}

export function resetPassword(token, password) {
  return jsonFetch("/api/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, password }),
  });
}

export function logout() {
  return jsonFetch("/api/auth/logout", { method: "POST" }).catch(() => {});
}

export function deleteAccount() {
  return jsonFetch("/api/auth/me", { method: "DELETE" });
}

// ---- entitlements / payments ----
export function getEntitlements() {
  return jsonFetch("/api/entitlements");
}

export function createCheckout(lookupKey) {
  return jsonFetch("/api/payments/checkout", {
    method: "POST",
    body: JSON.stringify({ lookup_key: lookupKey, origin_url: window.location.origin }),
  });
}

export function getPaymentStatus(sessionId) {
  return jsonFetch(`/api/payments/status/${sessionId}`);
}

export function createPortalSession() {
  return jsonFetch("/api/payments/portal", {
    method: "POST",
    body: JSON.stringify({ origin_url: window.location.origin }),
  });
}

// ---- onboarding + analytics ----
export function saveOnboarding(answers, completed = false) {
  return jsonFetch("/api/onboarding", {
    method: "POST",
    body: JSON.stringify({ answers, completed }),
  });
}

export function getOnboarding() {
  return jsonFetch("/api/onboarding");
}

export function getAdminAnalytics() {
  return jsonFetch("/api/admin/analytics");
}

export function track(name, props = {}) {
  // Fire-and-forget; analytics must never block or break a flow.
  return jsonFetch("/api/events", {
    method: "POST",
    body: JSON.stringify({ name, props }),
  }).catch(() => {});
}

export function sendSupport(payload) {
  return jsonFetch("/api/support", { method: "POST", body: JSON.stringify(payload) });
}

